import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { CheckCircle2, Mail, Phone, MapPin, ArrowRight, Lock } from 'lucide-react';
import { FormSelect } from '../components/FormSelect';
import { Breadcrumbs } from '../components/Breadcrumbs';
export function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    orgName: '',
    orgType: '',
    country: '',
    city: '',
    sector: '',
    enquiryType: '',
    productCategory: '',
    quantity: '',
    timescale: '',
    budgetHolder: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  return <>
      <PageHero label="Get in Touch" title="Contact Chiltern Global" subtitle="Our team is available to discuss manufacturing requirements, framework procurement, and product specifications." data-id="element-532" />

      <Breadcrumbs items={[{
      label: 'Contact'
    }]} data-id="element-533" />

      <section className="bg-white py-24" data-id="element-534">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-535">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-536">
            {/* Contact Info */}
            <div className="lg:col-span-1" data-id="element-537">
              <h2 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-538">
                Contact Information
              </h2>
              <div className="space-y-8" data-id="element-539">
                <div className="flex items-start gap-4" data-id="element-540">
                  <div className="w-10 h-10 bg-slate-50 border border-steel-200 flex items-center justify-center rounded-sm flex-shrink-0" data-id="element-541">
                    <Mail className="w-5 h-5 text-cg-orange-600" data-id="element-542" />
                  </div>
                  <div data-id="element-543">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-544">
                      Email
                    </h3>
                    <p className="text-steel-600" data-id="element-545">
                      enquiries@chilternglobal.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-546">
                  <div className="w-10 h-10 bg-slate-50 border border-steel-200 flex items-center justify-center rounded-sm flex-shrink-0" data-id="element-547">
                    <Phone className="w-5 h-5 text-cg-orange-600" data-id="element-548" />
                  </div>
                  <div data-id="element-549">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-550">
                      Phone
                    </h3>
                    <p className="text-steel-600" data-id="element-551">+44 (0) 1234 567890</p>
                    <p className="text-steel-500 text-sm mt-1" data-id="element-552">
                      Mon-Fri, 09:00 - 17:00 GMT
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-553">
                  <div className="w-10 h-10 bg-slate-50 border border-steel-200 flex items-center justify-center rounded-sm flex-shrink-0" data-id="element-554">
                    <MapPin className="w-5 h-5 text-cg-orange-600" data-id="element-555" />
                  </div>
                  <div data-id="element-556">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-557">
                      Head Office
                    </h3>
                    <p className="text-steel-600" data-id="element-558">
                      Unit 12, Industrial Park
                      <br data-id="element-559" />
                      Hertfordshire
                      <br data-id="element-560" />
                      United Kingdom
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-navy-900 rounded-sm text-white" data-id="element-561">
                <h3 className="font-bold mb-2 flex items-center gap-2" data-id="element-562">
                  <Lock className="w-4 h-4 text-cg-orange-600" data-id="element-563" /> Secure &
                  Confidential
                </h3>
                <p className="text-sm text-steel-300 leading-relaxed" data-id="element-564">
                  All enquiries submitted through this form are transmitted via
                  encrypted channels and treated in strict confidence. Your
                  information will not be shared with third parties. For
                  sensitive or classified requirements, please indicate this in
                  your message and we will arrange additional secure
                  communication channels.
                </p>
              </div>
            </div>

            {/* Structured Contact Form */}
            <div className="lg:col-span-2" data-id="element-565">
              {!submitted ? <form onSubmit={handleSubmit} className="bg-slate-50 p-8 md:p-10 border border-steel-200 rounded-sm space-y-10" data-id="element-566">
                  <div data-id="element-567">
                    <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-568">
                      Send an Enquiry
                    </h2>
                    <p className="text-steel-600 mb-8" data-id="element-569">
                      Please complete the form below to route your enquiry to
                      the correct department.
                    </p>
                  </div>

                  {/* Section A: Contact Details */}
                  <div data-id="element-570">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-571">
                      Contact Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-572">
                      <div data-id="element-573">
                        <label htmlFor="firstName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-574">
                          First Name *
                        </label>
                        <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-575" />
                      </div>
                      <div data-id="element-576">
                        <label htmlFor="lastName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-577">
                          Last Name *
                        </label>
                        <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-578" />
                      </div>
                      <div data-id="element-579">
                        <label htmlFor="email" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-580">
                          Work Email *
                        </label>
                        <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-581" />
                      </div>
                      <div data-id="element-582">
                        <label htmlFor="phone" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-583">
                          Telephone
                        </label>
                        <input type="tel" id="phone" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.phone} onChange={handleChange} data-id="element-584" />
                      </div>
                      <div className="md:col-span-2" data-id="element-585">
                        <label htmlFor="jobTitle" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-586">
                          Job Title
                        </label>
                        <input type="text" id="jobTitle" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.jobTitle} onChange={handleChange} data-id="element-587" />
                      </div>
                    </div>
                  </div>

                  {/* Section B: Organisation Details */}
                  <div data-id="element-588">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-589">
                      Organisation Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-590">
                      <div className="md:col-span-2" data-id="element-591">
                        <label htmlFor="orgName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-592">
                          Organisation Name *
                        </label>
                        <input type="text" id="orgName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.orgName} onChange={handleChange} data-id="element-593" />
                      </div>
                      <div data-id="element-594">
                        <FormSelect id="orgType" label="Organisation Type" required value={formData.orgType} onChange={handleChange} data-id="element-595">
                          <option value="" data-id="element-596">Select Type</option>
                          <option value="Public sector buyer" data-id="element-597">
                            Public sector buyer
                          </option>
                          <option value="Defence buyer" data-id="element-598">Defence buyer</option>
                          <option value="Police force" data-id="element-599">Police force</option>
                          <option value="Custodial/prison service" data-id="element-600">
                            Custodial/prison service
                          </option>
                          <option value="Medical/NGO procurement" data-id="element-601">
                            Medical/NGO procurement
                          </option>
                          <option value="Private sector" data-id="element-602">Private sector</option>
                          <option value="Distributor" data-id="element-603">Distributor</option>
                          <option value="Other" data-id="element-604">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-605">
                        <label htmlFor="country" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-606">
                          Country *
                        </label>
                        <input type="text" id="country" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.country} onChange={handleChange} data-id="element-607" />
                      </div>
                      <div data-id="element-608">
                        <label htmlFor="city" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-609">
                          City
                        </label>
                        <input type="text" id="city" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.city} onChange={handleChange} data-id="element-610" />
                      </div>
                    </div>
                  </div>

                  {/* Section C: Enquiry Routing */}
                  <div data-id="element-611">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-612">
                      Enquiry Routing
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-613">
                      <div data-id="element-614">
                        <FormSelect id="sector" label="Sector" required value={formData.sector} onChange={handleChange} data-id="element-615">
                          <option value="" data-id="element-616">Select Sector</option>
                          <option value="Defence" data-id="element-617">Defence</option>
                          <option value="Police" data-id="element-618">Police</option>
                          <option value="Prisons" data-id="element-619">Prisons</option>
                          <option value="Medical" data-id="element-620">Medical</option>
                          <option value="Other" data-id="element-621">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-622">
                        <FormSelect id="enquiryType" label="Enquiry Type" required value={formData.enquiryType} onChange={handleChange} data-id="element-623">
                          <option value="" data-id="element-624">Select Type</option>
                          <option value="Product enquiry" data-id="element-625">
                            Product enquiry
                          </option>
                          <option value="Framework enquiry" data-id="element-626">
                            Framework enquiry
                          </option>
                          <option value="Manufacturing enquiry" data-id="element-627">
                            Manufacturing enquiry
                          </option>
                          <option value="Design and development enquiry" data-id="element-628">
                            Design and development enquiry
                          </option>
                          <option value="Documentation request" data-id="element-629">
                            Documentation request
                          </option>
                          <option value="Catalogue request" data-id="element-630">
                            Catalogue request
                          </option>
                          <option value="Other" data-id="element-631">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-632">
                        <FormSelect id="productCategory" label="Product/Category of Interest" value={formData.productCategory} onChange={handleChange} data-id="element-633">
                          <option value="" data-id="element-634">Select Category (Optional)</option>
                          <option value="Bags and Backpacks" data-id="element-635">
                            Bags and Backpacks
                          </option>
                          <option value="Clothing" data-id="element-636">Clothing</option>
                          <option value="Fire Retardants" data-id="element-637">
                            Fire Retardants
                          </option>
                          <option value="Public Order" data-id="element-638">Public Order</option>
                          <option value="Tactical Vests" data-id="element-639">Tactical Vests</option>
                          <option value="Training Equipment" data-id="element-640">
                            Training Equipment
                          </option>
                          <option value="Other" data-id="element-641">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-642">
                        <FormSelect id="quantity" label="Quantity/Scale" value={formData.quantity} onChange={handleChange} data-id="element-643">
                          <option value="" data-id="element-644">Select Scale (Optional)</option>
                          <option value="Single item" data-id="element-645">Single item</option>
                          <option value="Small batch" data-id="element-646">Small batch</option>
                          <option value="Ongoing supply" data-id="element-647">Ongoing supply</option>
                          <option value="Large procurement" data-id="element-648">
                            Large procurement
                          </option>
                        </FormSelect>
                      </div>
                      <div data-id="element-649">
                        <FormSelect id="timescale" label="Timescale" value={formData.timescale} onChange={handleChange} data-id="element-650">
                          <option value="" data-id="element-651">Select Timescale (Optional)</option>
                          <option value="Immediate" data-id="element-652">Immediate</option>
                          <option value="1 to 4 weeks" data-id="element-653">1 to 4 weeks</option>
                          <option value="1 to 3 months" data-id="element-654">1 to 3 months</option>
                          <option value="3 or more months" data-id="element-655">
                            3 or more months
                          </option>
                        </FormSelect>
                      </div>
                      <div data-id="element-656">
                        <label htmlFor="budgetHolder" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-657">
                          Budget Holder Email
                        </label>
                        <input type="email" id="budgetHolder" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.budgetHolder} onChange={handleChange} data-id="element-658" />
                      </div>
                    </div>
                  </div>

                  {/* Section D: Message */}
                  <div data-id="element-659">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-660">
                      Message
                    </h3>
                    <div data-id="element-661">
                      <label htmlFor="message" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-662">
                        Message / Requirements *
                      </label>
                      <textarea id="message" required rows={5} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" placeholder="Please include specification/configuration, intended use sector, quantity, delivery region, and procurement constraints..." value={formData.message} onChange={handleChange} data-id="element-663" />
                    </div>
                  </div>

                  <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors" data-id="element-664">
                    Submit Enquiry
                  </button>

                  <div className="flex items-start gap-2 bg-white border border-steel-200 rounded-sm p-3" data-id="element-665">
                    <Lock className="w-3.5 h-3.5 text-cg-orange-600 mt-0.5 flex-shrink-0" data-id="element-666" />
                    <p className="text-xs text-steel-500 leading-relaxed" data-id="element-667">
                      This form is encrypted and all submissions are treated in
                      strict confidence. Your data is processed in accordance
                      with our{' '}
                      <Link to="/privacy-policy" className="underline hover:text-cg-orange-600" data-id="element-668">
                        Privacy Policy
                      </Link>{' '}
                      and{' '}
                      <Link to="/terms-of-service" className="underline hover:text-cg-orange-600" data-id="element-669">
                        Terms of Service
                      </Link>
                      .
                    </p>
                  </div>
                </form> : <div className="bg-green-50 border border-green-200 p-12 rounded-sm text-center h-full flex flex-col items-center justify-center" data-id="element-670">
                  <CheckCircle2 className="w-16 h-16 text-green-600 mb-6" data-id="element-671" />
                  <h3 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-672">
                    Thank you. Your submission has been received.
                  </h3>
                  <p className="text-steel-700 mb-8 max-w-md mx-auto" data-id="element-673">
                    Your enquiry has been routed to the relevant department. We
                    will review your requirements and be in touch.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-674">
                    <Link to="/products" className="px-6 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-navy-800 transition-colors" data-id="element-675">
                      View Products
                    </Link>
                    <Link to="/frameworks" className="px-6 py-3 bg-white border border-navy-900 text-navy-900 font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-slate-50 transition-colors" data-id="element-676">
                      View Frameworks
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </section>
    </>;
}